/* eslint-disable react/jsx-no-target-blank */
import { AmericanFlag, PortugueseFlag, SpanishFlag } from './assets';
import { ArrowUpRight, ChevronDown, LogOut } from 'lucide-react';
import {
  DropdownMenuContentChildren,
  Header,
  HeaderElement,
  Text
} from '@pulsarml/avalon-ui';
import { I18n, Translate } from '@utils/i18n';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CompanySelection from '_clean-code/components/CompanySelection';
import MachineGroupSelector from '_clean-code/components/MachineGroupSelector';
import WhatsNew from '../WhatsNew';
import cn from 'classnames';
import { sendEventTrack } from '@utils/analytics';
import { setTranslationLocale } from 'ducks/i18n/actions';
import styles from './index.module.scss';
import { toggleSidebarCollapsed } from 'ducks/config/actions';
import { useNavigate } from 'react-router-dom';
import { userSelector } from 'ducks/main-dashboard/selectors';
import { useAuth } from '_clean-code/api/hooks/useAuth';
import { TvModeButton } from './TvModeButton';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { AlertsBellButton } from '_clean-code/components/AlertsBellButton';

interface AppTopBarProps {
  classNames?: string;
  title?: string;
  titleClassName?: string;
  titleTextClassName?: string;
}

const MetabaseRoutesRegex = /\/analytics\/.*-\d+/;

const LANGUAGE_OPTIONS = [
  { locale: 'us', label: 'english', Flag: AmericanFlag },
  { locale: 'es', label: 'spanish', Flag: SpanishFlag },
  { locale: 'pt', label: 'portuguese', Flag: PortugueseFlag }
];

function AppTopBar(props: AppTopBarProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [isMachineGroupOpen, setIsMachineGroupOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(true);
  const currentUser = useSelector(userSelector);
  const { logout } = useAuth();
  const { realtimeV2, alertsV2 } = useFeatureFlags();

  const route = window.location.pathname;
  const metabaseRoutes = MetabaseRoutesRegex.test(route);

  return (
    <>
      <Header
        title={props.title!}
        userName={currentUser.name ?? ''}
        titleClassName={props.titleClassName}
        titleTextClassName={props.titleTextClassName}
        onHamburgerClick={() => {
          dispatch(toggleSidebarCollapsed());
        }}
        onLogoClick={() => {
          navigate('/');
        }}
        onOpenChange={(open) => {
          if (open) {
            setIsMachineGroupOpen(false);
            setShowModal(false);
          }
        }}
        logoContainerClassName="!tw-mr-[75px]"
        data-testid="app-top-bar-header"
        //titleClassName="!tw-bg-red-500"
        //titleContainerClassName="!tw-bg-red-500"
      >
        <HeaderElement>
          <div className={styles.rightPanel}>
            {/* TODO: find a better way to check if the route is realtimeV2 it was failing with /realtime/ */}
            {realtimeV2 && route.includes('realtime') && <TvModeButton />}
            <CompanySelection className={styles.companySelection} />
            {!metabaseRoutes && (
              <div className={styles.machineGroup}>
                <MachineGroupSelector
                  classNames={styles.machineGroupSelector}
                  isOpen={isMachineGroupOpen}
                  setIsOpen={setIsMachineGroupOpen}
                />
              </div>
            )}
            {/* AlertsBellButton must remain last — directly left of user avatar */}
            {alertsV2 && <AlertsBellButton />}
          </div>
        </HeaderElement>
        <DropdownMenuContentChildren>
          <div
            className={cn(
              styles.dropdownContent,
              'tw-flex tw-flex-col tw-min-w-[200px] tw-max-w-[250px]',
              isLanguageMenuOpen ? 'tw-max-h-[360px]' : 'tw-max-h-[289px]'
            )}
          >
            <div
              className="tw-flex tw-flex-col tw-gap-1 tw-pt-16px tw-px-12px tw-pb-8px tw-text-ellipsis tw-text-nowrap"
              data-testid="user-info"
            >
              <Text
                variant="normal"
                className="tw-font-bold tw-text-ellipsis tw-w-full"
                lineHeight="20px"
              >
                {currentUser.name}
              </Text>
              <Text
                variant="label"
                className="tw-text-sm tw-text-[#6B7280] tw-text-ellipsis tw-w-full"
                lineHeight="16px"
              >
                {currentUser.email}
              </Text>
            </div>
            <div
              className="tw-py-12px tw-cursor-pointer tw-px-12px"
              onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
              data-testid="language-menu-toggle"
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                <Text variant="normal" className="tw-text-sm" lineHeight="20px">
                  <Translate value="language" />
                </Text>
                <ChevronDown
                  className={cn(
                    'tw-h-4 tw-w-4',
                    styles.chevron,
                    isLanguageMenuOpen && styles.rotate
                  )}
                />
              </div>
            </div>
            {isLanguageMenuOpen && (
              <div className="tw-px-12px">
                {LANGUAGE_OPTIONS.map(({ locale, label, Flag }) => (
                  <div
                    key={locale}
                    className={cn(
                      'tw-py-8px tw-px-12px tw-flex tw-justify-between tw-items-center tw-gap-8px tw-cursor-pointer tw-rounded-sm',
                      I18n._locale === locale && 'tw-bg-gray-100'
                    )}
                    onClick={() => {
                      sendEventTrack('Switched Language', {
                        language: locale,
                        companyName: currentUser.companies?.[0]?.name
                      });
                      setTranslationLocale(locale);
                      localStorage.setItem('i18n-locale', locale);
                      window.location.reload();
                    }}
                    data-testid={`language-option-${locale}`}
                  >
                    <Flag />
                    <div className="tw-flex-1">
                      <Text
                        variant="label"
                        className="tw-text-sm tw-text-[#6B7280]"
                        lineHeight="20px"
                      >
                        <Translate value={label} />
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div
              className="tw-py-12px tw-cursor-pointer tw-px-12px"
              onClick={() => {
                navigate('/create-ticket');
              }}
              data-testid="support-center-link"
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                <Text variant="normal" className="tw-text-sm" lineHeight="16px">
                  <Translate value="support_center" />
                </Text>
                <ArrowUpRight className="tw-h-4 tw-w-4" />
              </div>
            </div>
            <a
              href={
                I18n._locale === 'es'
                  ? 'https://docs.pulsarml.com/auth/start?returnTo=/es/guides'
                  : 'https://docs.pulsarml.com/auth/start?returnTo=/guides'
              }
              target="_blank"
              className="tw-block tw-py-12px tw-cursor-pointer tw-border-b tw-border-b-gray-200 tw-px-12px tw-no-underline tw-text-inherit active:tw-text-black hover:tw-text-black hover:tw-no-underline"
              data-testid="help-docs-link"
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                <Text variant="normal" className="tw-text-sm" lineHeight="20px">
                  <Translate value="help_and_docs" />
                </Text>
                <ArrowUpRight className="tw-h-4 tw-w-4" />
              </div>
            </a>
            <div
              className="tw-py-12px tw-cursor-pointer tw-px-12px"
              onClick={() => logout.mutate()}
              data-testid="logout-button"
            >
              <div className="tw-flex tw-items-center tw-justify-start tw-gap-8px">
                <LogOut className="tw-h-4 tw-w-4" />
                <Text variant="normal" className="tw-text-sm" lineHeight="20px">
                  <Translate value="logout" />
                </Text>
              </div>
            </div>
          </div>
        </DropdownMenuContentChildren>
      </Header>
      <WhatsNew showModal={showModal} setShowModal={setShowModal} />
    </>
  );
}

export default AppTopBar;
